import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AnalysisResult from './AnalysisResult';

const LegalLoader = () => (
  <div className="legal-loader-compact">
    <div className="spinner-mini"></div>
    <span>Analyzing Legal Data</span>
    <style jsx>{`
      .legal-loader-compact {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 100px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.05);
      }
      .spinner-mini {
        width: 14px;
        height: 14px;
        border: 2px solid #e2e8f0;
        border-top: 2px solid #2563eb;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      span {
        font-size: 12px;
        font-weight: 700;
        color: #1e293b;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    `}</style>
  </div>
);

const ChatWindow = ({ messages, onSendMessage, isLoading }) => {
  const [input, setInput] = useState('');
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSend = () => {
    if (input.trim() && !isLoading) {
      onSendMessage(input);
      setInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  return (
    <div className="chat-interface">
      <div className="messages-scroll">
        <div className="welcome-header pb-5">
          <h1 style={{ color: "#002785", textAlign: "center", fontWeight: "bolder" }}>Intelligent Legal Strategy Assistant</h1>
          <p style={{ fontWeight: "bold" }}>Transforming complex case data into actionable strategic insights and outcome predictions with high-precision AI analysis.</p>
        </div>
        <div className="messages-container">
          {messages.map((msg, index) => (
            <div key={index} className={`msg-row ${msg.role}`}>
              <div className="msg-bubble">
                {msg.role === 'assistant' ? (
                  <AnalysisResult content={msg.content} />
                ) : (
                  <div className="user-text">{msg.content}</div>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="msg-row loader-centered">
              <LegalLoader />
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>

      <div className="input-floating-area">
        <div className="input-wrapper-compact">
          <input
            type="text"
            placeholder="Message CasePredictAI..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button
            className="send-btn-minimal"
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
          >
            <FontAwesomeIcon icon="paper-plane" />
          </button>
        </div>
      </div>

      <style jsx>{`
        .chat-interface {
          display: flex;
          flex-direction: column;
          height: 100%;
          padding:20px;
          background: #f0f0f0;
          position: relative;
        }
        .welcome-header {
          text-align: center;
        }
        .messages-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 60px 10px;
          padding-bottom: 70px;
        }

        .messages-container {
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          background: #f0f0f0;
          height:100%
        }

        .msg-row {
          display: flex;
          width: 100%;
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .msg-row.user {
          justify-content: flex-end;
        }

        .msg-row.loader-centered {
          justify-content: center;
          padding: 20px 0;
        }

        .msg-bubble {
          max-width: 100%;
        }

        .user-text {
          background: #2563eb;
          color: #ffffff;
          padding: 10px 16px;
          border-radius: 18px;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.5;
          box-shadow: 0 1px 2px rgba(0,0,0,0.02);
          max-width: 40vw;
        }

        .input-floating-area {
          padding: 5px;
          display: flex;
          justify-content: center;
        }

        .input-wrapper-compact {
          width: 100%;
          max-width: 760px;
          background: #ffffff;
          border-radius: 12px;
          padding: 6px 6px 6px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.03), 0 1px 2px rgba(0,0,0,0.02);
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .input-wrapper-compact:focus-within {
          border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.05), 0 4px 12px rgba(0,0,0,0.03);
        }

        .input-wrapper-compact input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 14px;
          color: #0f172a;
          padding: 8px 0;
        }

        .input-wrapper-compact input:focus {
          outline: none;
        }

        .send-btn-minimal {
          width: 32px;
          height: 32px;
          border: none;
          background: #2563eb;
          color: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          cursor: pointer;
          transition: transform 0.1s, background 0.2s;
        }

        .send-btn-minimal:hover:not(:disabled) {
          background: #1d4ed8;
          transform: scale(1.05);
        }

        .send-btn-minimal:active:not(:disabled) {
          transform: scale(0.95);
        }

        .send-btn-minimal:disabled {
          background: #f1f5f9;
          color: #cbd5e1;
          cursor: not-allowed;
        }

        /* Custom Scrollbar */
        .messages-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .messages-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .messages-scroll::-webkit-scrollbar-thumb {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .messages-scroll::-webkit-scrollbar-thumb:hover {
          background: #e2e8f0;
        }

        @media (max-width: 768px) {
          .messages-container {
            padding: 0 16px;
          }
          .input-floating-area {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatWindow;
